import { useContext } from "react";
import { Helmet } from "react-helmet-async";
import ContentText from "../components/contentText.jsx";
import JobCard from "../components/jobCard.jsx";
import PageHero from "../components/pageHero.jsx";
import SectionContent from "../components/sectionContent.jsx";
import { JobsContext } from "../context/JobsContext";
import { SiteContentContext } from "../context/SiteContentContext.jsx";
import { useLocalePath } from "../hooks/useLocalePath";
import {
  absoluteUrl,
  DEFAULT_COMPANY_NAME,
  LOGO_URL,
  ORGANIZATION_ID,
  SITE_ORIGIN,
} from "../seoUtils";

const JobOffers = () => {
  const { jobs } = useContext(JobsContext);
  const { getContent } = useContext(SiteContentContext);
  const { locale } = useLocalePath();
  const sectionIntro = getContent("careers.section_intro", "");
  const jobsUrl = absoluteUrl(locale, "/jobs");

  // One real JobPosting per published ad. Google rejects a JobPosting without datePosted, so
  // ads missing createdAt are skipped rather than emitted as an invalid stub.
  const jobPostings = jobs
    .filter((job) => job.published !== false && job.title && job.createdAt)
    .map((job, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "JobPosting",
        title: job.title,
        description: job.teaser || job.body || job.title,
        datePosted: new Date(job.createdAt).toISOString(),
        employmentType: job.workType || undefined,
        url: absoluteUrl(locale, `/jobs/${encodeURIComponent(job.slug)}`),
        hiringOrganization: {
          "@type": "Organization",
          "@id": ORGANIZATION_ID,
          name: DEFAULT_COMPANY_NAME,
          sameAs: SITE_ORIGIN,
          logo: LOGO_URL,
        },
        jobLocation: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            ...(job.location ? { addressLocality: job.location } : {}),
            addressCountry: "SE",
          },
        },
      },
    }));

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>AS Maskiner - Lediga jobb | Karriär hos Sveriges ledande maskinåterförsäljare</title>
        <meta
          name="description"
          content="Lediga jobb på AS Maskiner - Sveriges ledande leverantör av maskiner för trä- och plastindustrin. Vi söker engagerade medarbetare med passion för CNC-maskiner, kantlistmaskiner och träbearbetning. Bli en del av vårt växande team!"
        />
        <meta
          name="keywords"
          content="jobb AS Maskiner, lediga jobb, karriär maskinbranschen, jobb CNC maskiner, jobb försäljning maskiner, maskintekniker jobb, Sverige"
        />

        {/* Open Graph */}
        <meta property="og:url" content={jobsUrl} />
        <meta property="og:title" content="Lediga jobb - AS Maskiner" />
        <meta
          property="og:description"
          content="Bli en del av Sveriges ledande maskinleverantör för trä- och plastindustrin. Se våra lediga tjänster."
        />
        {jobPostings.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              url: jobsUrl,
              name: "Lediga jobb på AS Maskiner",
              numberOfItems: jobPostings.length,
              itemListElement: jobPostings,
            })}
          </script>
        )}
      </Helmet>

      <PageHero
        titleKey="careers.hero_title"
        titleFallback="Vill du ta nästa steg i karriären?"
        subtitleKey="careers.hero_subtitle"
        subtitleFallback="Gör det tillsammans med oss!"
        backgroundKey="careers.hero_background"
        alignment="bottom"
      />

      <section className="py-16 bg-white">
        <SectionContent>
          <h2 className="text-3xl md:text-4xl font-bold mb-2">
            <ContentText contentKey="careers.section_title" fallback="Lediga tjänster" />
          </h2>
          {sectionIntro && (
            <p className="text-base text-text-muted mb-2">
              <ContentText contentKey="careers.section_intro" fallback="" />
            </p>
          )}
          <div className="space-y-6 mt-8">
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </SectionContent>
      </section>
    </div>
  );
};

export default JobOffers;
